/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { IconButton } from "@/components/ui/icon-button";
import { useCompany } from "@/hooks/useCompany";
import { TRIAL_READ_ONLY_MESSAGE, useTrialMode } from "@/hooks/useTrialMode";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Upload, Search, Grid3X3, List, Trash2,
  Copy, Loader2, Tags, X, Sparkles, BarChart3
} from "lucide-react";
import { Link } from "react-router-dom";
import { MediaAsset, formatSize, getImageDimensions } from "@/components/media/types";
import MediaSidebar from "@/components/media/MediaSidebar";
import MediaGrid from "@/components/media/MediaGrid";
import MediaUploadZone from "@/components/media/MediaUploadZone";
import MediaSourcePanel from "@/components/media/MediaSourcePanel";
import BulkMetadataDialog from "@/components/media/BulkMetadataDialog";
import MediaPagination from "@/components/media/MediaPagination";
import AdminSeedButton from "@/components/media/AdminSeedButton";

const tenantDb = supabase as any;

export default function MediaLibraryPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { currentCompanyId, isLoading: companyLoading } = useCompany();
  const trialMode = useTrialMode();
  const { toast } = useToast();
  const [assets, setAssets] = useState<MediaAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [searchQuery, setSearchQuery] = useState("");
  const [showUpload, setShowUpload] = useState(false);
  const [showDiscover, setShowDiscover] = useState(false);
  const [previewAsset, setPreviewAsset] = useState<MediaAsset | null>(null);
  const [editAsset, setEditAsset] = useState<MediaAsset | null>(null);
  const [editAlt, setEditAlt] = useState("");
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [activeTags, setActiveTags] = useState<string[]>([]);
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 50;
  // Fetch assets
  const fetchAssets = useCallback(async () => {
    if (!user || !currentCompanyId) return;
    setLoading(true);
    let allData: any[] = [];
    let from = 0;
    const FETCH_SIZE = 1000;
    while (true) {
      const { data, error } = await tenantDb
        .from("media_assets")
        .select("*")
        .eq("company_id", currentCompanyId)
        .order("created_at", { ascending: false })
        .range(from, from + FETCH_SIZE - 1);
      if (error) {
        toast({ title: "Error", description: error.message, variant: "destructive" });
        break;
      }
      allData = allData.concat(data || []);
      if (!data || data.length < FETCH_SIZE) break;
      from += FETCH_SIZE;
    }
    setAssets(allData.map((a: any) => ({ ...a, selected: false })));
    setLoading(false);
  }, [currentCompanyId, user, toast]);

  useEffect(() => { fetchAssets(); }, [fetchAssets]);

  // Check admin role
  useEffect(() => {
    if (!user) return;
    supabase.rpc("has_role", { _user_id: user.id, _role: "admin" }).then(({ data }) => {
      setIsAdmin(!!data);
    });
  }, [user]);

  // Computed: category counts
  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    assets.forEach((a) => {
      const cat = a.folder || "uncategorized";
      counts[cat] = (counts[cat] || 0) + 1;
    });
    return counts;
  }, [assets]);

  // Computed: all tags with counts
  const allTags = useMemo(() => {
    const tagMap: Record<string, number> = {};
    assets.forEach((a) => {
      (a.tags || []).forEach((t) => {
        tagMap[t] = (tagMap[t] || 0) + 1;
      });
    });
    return Object.entries(tagMap)
      .map(([tag, count]) => ({ tag, count }))
      .sort((a, b) => b.count - a.count);
  }, [assets]);

  // Smart filtering with prefix support
  const filteredAssets = useMemo(() => {
    let filtered = [...assets];

    // Category filter
    if (activeCategory) {
      filtered = filtered.filter((a) => (a.folder || "uncategorized") === activeCategory);
    }

    // Tag filter
    if (activeTags.length > 0) {
      filtered = filtered.filter((a) =>
        activeTags.every((t) => (a.tags || []).includes(t))
      );
    }

    // Search with prefix support
    if (searchQuery) {
      const q = searchQuery.toLowerCase().trim();

      if (q.startsWith("tag:")) {
        const tagQuery = q.slice(4).trim();
        if (tagQuery) {
          filtered = filtered.filter((a) =>
            (a.tags || []).some((t) => t.includes(tagQuery))
          );
        }
      } else if (q.startsWith("cat:")) {
        const catQuery = q.slice(4).trim();
        if (catQuery) {
          filtered = filtered.filter((a) =>
            (a.folder || "uncategorized").includes(catQuery)
          );
        }
      } else {
        filtered = filtered.filter((a) =>
          a.file_name.toLowerCase().includes(q) ||
          (a.alt_text || "").toLowerCase().includes(q) ||
          (a.tags || []).some((t) => t.includes(q))
        );
      }
    }

    return filtered;
  }, [assets, activeCategory, activeTags, searchQuery]);

  // Reset page when filters change
  useEffect(() => { setCurrentPage(1); }, [activeCategory, activeTags, searchQuery]);

  const totalPages = Math.ceil(filteredAssets.length / PAGE_SIZE);
  const paginatedAssets = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredAssets.slice(start, start + PAGE_SIZE);
  }, [filteredAssets, currentPage, PAGE_SIZE]);

  const selectedCount = assets.filter((a) => a.selected).length;
  const blockTrialMutation = () => {
    toast({ title: "Trial workspace", description: TRIAL_READ_ONLY_MESSAGE, variant: "destructive" });
  };

  // Upload handler
  const handleUpload = async (files: FileList, category: string, tags: string[]) => {
    if (trialMode.isReadOnly) {
      blockTrialMutation();
      return;
    }
    if (!user || !currentCompanyId || !files.length) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const ext = file.name.split(".").pop();
        const path = `${user.id}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
        const { error: uploadErr } = await supabase.storage.from("media").upload(path, file);
        if (uploadErr) throw uploadErr;

        const { data: urlData } = supabase.storage.from("media").getPublicUrl(path);

        let width: number | null = null;
        let height: number | null = null;
        if (file.type.startsWith("image/")) {
          const dims = await getImageDimensions(file);
          width = dims.width;
          height = dims.height;
        }

        const { error: dbErr } = await tenantDb.from("media_assets").insert({
          company_id: currentCompanyId,
          user_id: user.id,
          file_name: file.name,
          file_path: path,
          file_url: urlData.publicUrl,
          file_size: file.size,
          mime_type: file.type,
          width,
          height,
          folder: category,
          tags,
        });
        if (dbErr) throw dbErr;
      }
      toast({ title: "Upload successful" });
      fetchAssets();
      setShowUpload(false);
    } catch (err: any) {
      toast({ title: "Upload error", description: err.message, variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (asset: MediaAsset) => {
    if (trialMode.isReadOnly) {
      blockTrialMutation();
      return;
    }
    const { error: storageErr } = await supabase.storage.from("media").remove([asset.file_path]);
    if (storageErr) {
      toast({ title: "Error", description: storageErr.message, variant: "destructive" });
      return;
    }
    const { error: dbErr } = await tenantDb.from("media_assets").delete().eq("id", asset.id).eq("company_id", currentCompanyId);
    if (dbErr) {
      toast({ title: "Error", description: dbErr.message, variant: "destructive" });
      return;
    }
    setAssets((prev) => prev.filter((a) => a.id !== asset.id));
    toast({ title: "File deleted" });
  };

  const handleBulkDelete = async () => {
    const selected = assets.filter((a) => a.selected);
    for (const asset of selected) {
      await handleDelete(asset);
    }
  };

  const handleBulkMetadata = async (updates: { folder?: string; tags?: string[]; mode: "replace" | "append" }) => {
    if (trialMode.isReadOnly) {
      blockTrialMutation();
      return;
    }
    const selected = assets.filter((a) => a.selected);
    for (const asset of selected) {
      const patch: { folder?: string; tags?: string[] } = {};
      if (updates.folder) patch.folder = updates.folder;
      if (updates.tags) {
        patch.tags = updates.mode === "append"
          ? [...new Set([...(asset.tags || []), ...updates.tags])]
          : updates.tags;
      }
      await tenantDb.from("media_assets").update(patch).eq("id", asset.id).eq("company_id", currentCompanyId);
    }
    toast({ title: `${selected.length} file(s) updated` });
    fetchAssets();
  };

  const handleSelect = (id: string) => {
    setAssets((prev) => prev.map((a) => (a.id === id ? { ...a, selected: !a.selected } : a)));
  };

  const handleSelectAll = () => {
    const allSelected = filteredAssets.every((a) => a.selected);
    const ids = new Set(filteredAssets.map((a) => a.id));
    setAssets((prev) => prev.map((a) => (ids.has(a.id) ? { ...a, selected: !allSelected } : a)));
  };

  const handleCopyUrl = (url: string) => {
    navigator.clipboard.writeText(url);
    toast({ title: "URL copied" });
  };

  const handleSaveAlt = async () => {
    if (!editAsset) return;
    if (trialMode.isReadOnly) {
      blockTrialMutation();
      return;
    }
    const { error } = await tenantDb.from("media_assets").update({ alt_text: editAlt }).eq("id", editAsset.id).eq("company_id", currentCompanyId);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      return;
    }
    setAssets((prev) => prev.map((a) => (a.id === editAsset.id ? { ...a, alt_text: editAlt } : a)));
    setEditAsset(null);
    toast({ title: "Alt text saved" });
  };

  const handleTagToggle = (tag: string) => {
    setActiveTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  if (companyLoading) {
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="flex h-[calc(100vh-64px)]">
      {/* Sidebar */}
      <MediaSidebar
        categoryCounts={categoryCounts}
        activeCategory={activeCategory}
        onCategorySelect={setActiveCategory}
        allTags={allTags}
        activeTags={activeTags}
        onTagToggle={handleTagToggle}
      />

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <div>
            <h1 className="text-xl font-bold text-foreground">Media Library</h1>
            <p className="text-xs text-muted-foreground">
              {filteredAssets.length} file{filteredAssets.length !== 1 ? "s" : ""}
              {activeCategory && ` in ${activeCategory}`}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {isAdmin && user && (
              <AdminSeedButton userId={user.id} onComplete={fetchAssets} />
            )}
            <Button asChild size="sm" variant="ghost">
              <Link to="/media-library/analytics">
                <BarChart3 className="h-4 w-4 mr-1" />Analytics
              </Link>
            </Button>
            <Button size="sm" variant="outline" onClick={() => trialMode.isReadOnly ? blockTrialMutation() : (() => { setShowDiscover((v) => !v); setShowUpload(false); })()} disabled={trialMode.isReadOnly} title={trialMode.isReadOnly ? TRIAL_READ_ONLY_MESSAGE : undefined}>
              <Sparkles className="h-4 w-4 mr-1" />Discover
            </Button>
            <Button size="sm" onClick={() => trialMode.isReadOnly ? blockTrialMutation() : (() => { setShowUpload((v) => !v); setShowDiscover(false); })()} disabled={trialMode.isReadOnly} title={trialMode.isReadOnly ? TRIAL_READ_ONLY_MESSAGE : undefined}>
              <Upload className="h-4 w-4 mr-1" />Upload
            </Button>
          </div>
        </div>

        {/* Upload Zone */}
        {showUpload && (
          <div className="px-5 pt-4 relative">
            <IconButton
              variant="ghost"
              className="absolute top-6 right-7 z-10 h-6 w-6"
              onClick={() => setShowUpload(false)}
              label={t("actions.close")}
            >
              <X className="h-4 w-4" />
            </IconButton>
            <MediaUploadZone onUpload={handleUpload} uploading={uploading} />
          </div>
        )}

        {/* Discover (external + AI) */}
        {showDiscover && (
          <div className="px-5 pt-4 relative">
            <IconButton
              variant="ghost"
              className="absolute top-6 right-7 z-10 h-6 w-6"
              onClick={() => setShowDiscover(false)}
              label={t("actions.close")}
            >
              <X className="h-4 w-4" />
            </IconButton>
            <MediaSourcePanel
              defaultCategory={activeCategory || undefined}
              onImported={() => { fetchAssets(); }}
            />
          </div>
        )}

        {/* Toolbar */}
        <div className="flex items-center gap-3 px-5 py-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search... (tag:modern, cat:icons)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-9 text-xs"
            />
          </div>
          <div className="flex border rounded-lg overflow-hidden">
            <IconButton variant={viewMode === "grid" ? "default" : "ghost"} className="h-9 w-9 rounded-none" onClick={() => setViewMode("grid")} label={t("actions.gridView")}>
              <Grid3X3 className="h-4 w-4" />
            </IconButton>
            <IconButton variant={viewMode === "list" ? "default" : "ghost"} className="h-9 w-9 rounded-none" onClick={() => setViewMode("list")} label={t("actions.listView")}>
              <List className="h-4 w-4" />
            </IconButton>
          </div>
          {selectedCount > 0 && (
            <div className="flex items-center gap-2 ml-2 pl-4 border-l">
              <span className="text-xs text-muted-foreground">{selectedCount} selected</span>
              <Button variant="outline" size="sm" className="text-xs h-8" onClick={() => trialMode.isReadOnly ? blockTrialMutation() : setShowBulkEdit(true)} disabled={trialMode.isReadOnly} title={trialMode.isReadOnly ? TRIAL_READ_ONLY_MESSAGE : undefined}>
                <Tags className="h-3.5 w-3.5 mr-1" />Metadata
              </Button>
              <Button variant="outline" size="sm" className="text-xs h-8 text-destructive hover:text-destructive" onClick={handleBulkDelete} disabled={trialMode.isReadOnly} title={trialMode.isReadOnly ? TRIAL_READ_ONLY_MESSAGE : undefined}>
                <Trash2 className="h-3.5 w-3.5 mr-1" />Delete
              </Button>
            </div>
          )}
          <Button variant="ghost" size="sm" className="ml-auto text-xs" onClick={handleSelectAll}>
            {filteredAssets.length > 0 && filteredAssets.every((a) => a.selected) ? "Deselect All" : "Select All"}
          </Button>
        </div>

        {/* Grid/List */}
        <div className="flex-1 overflow-y-auto px-5 pb-5">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              <MediaGrid
                assets={paginatedAssets}
                viewMode={viewMode}
                onSelect={handleSelect}
                onPreview={setPreviewAsset}
                onCopyUrl={handleCopyUrl}
                onEditAlt={(a) => { setEditAsset(a); setEditAlt(a.alt_text || ""); }}
                onDelete={handleDelete}
              />
              <MediaPagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </>
          )}
        </div>
      </div>

      {/* Preview Dialog */}
      <Dialog open={!!previewAsset} onOpenChange={() => setPreviewAsset(null)}>
        <DialogContent className="max-w-3xl">
          {previewAsset && (
            <>
              <DialogHeader>
                <DialogTitle className="text-lg">{previewAsset.file_name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                {previewAsset.mime_type.startsWith("image/") ? (
                  <img src={previewAsset.file_url} alt={previewAsset.alt_text || previewAsset.file_name} className="w-full rounded-lg" />
                ) : (
                  <div className="aspect-video bg-muted rounded-lg flex items-center justify-center">
                    <span className="text-muted-foreground text-sm">{previewAsset.mime_type}</span>
                  </div>
                )}
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div><span className="text-muted-foreground">Size:</span> <span className="font-medium">{formatSize(previewAsset.file_size)}</span></div>
                  {previewAsset.width && previewAsset.height && (
                    <div><span className="text-muted-foreground">Dimensions:</span> <span className="font-medium">{previewAsset.width}×{previewAsset.height}</span></div>
                  )}
                  <div><span className="text-muted-foreground">Type:</span> <span className="font-medium">{previewAsset.mime_type}</span></div>
                </div>
                {previewAsset.alt_text && (
                  <div>
                    <span className="text-xs text-muted-foreground">Alt text:</span>
                    <p className="text-sm">{previewAsset.alt_text}</p>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => handleCopyUrl(previewAsset.file_url)}>
                  <Copy className="h-4 w-4 mr-1" />Copy URL
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Alt Text Dialog */}
      <Dialog open={!!editAsset} onOpenChange={() => setEditAsset(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Alt Text</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Describe the image for accessibility and SEO</Label>
            <Textarea value={editAlt} onChange={(e) => setEditAlt(e.target.value)} rows={3} placeholder="e.g. Team photo at product launch..." />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditAsset(null)}>Cancel</Button>
            <Button onClick={handleSaveAlt} disabled={trialMode.isReadOnly} title={trialMode.isReadOnly ? TRIAL_READ_ONLY_MESSAGE : undefined}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Metadata Dialog */}
      <BulkMetadataDialog
        open={showBulkEdit}
        onOpenChange={setShowBulkEdit}
        selectedCount={selectedCount}
        onApply={handleBulkMetadata}
      />
    </div>
  );
}
